<template>
  <section id="buy-zone" class="buy-zone-container">
    <div :class="{'h-screen': loading}">
      <div v-if="!loading">
        <pdp-no-product-data v-if="!locallyVariantProduct" @tryAgain="loadProductData" />
        <component
          :is="productDetailsComponent"
          v-else
          :product="locallyVariantProduct"
          :options="options"
          :copy-positioning-statement="copyPositioningStatement"
          :stock-message="stockMessage"
          :retailer-data="retailerData"
          :is-location-postal-code-correct="isLocationPostalCodeCorrect"
          :is-pdp="isPdp"
        >
          <template #consumer-messages>
            <slot name="consumer-messages"></slot>
          </template>
        </component>
      </div>
    </div>
  </section>
</template>

<script>
import {ProductApi} from '@/api/product';
import SchemaMarkup from '@/utils/schema-markup';
import BreadCrumbMarkup from '@/utils/richSearchResults/breadcrumb-markup';
import B2bProductDetails from '@/components/containers/buy-zone/b2b/ProductDetails';
import B2cProductDetails from '@/components/containers/buy-zone/b2c/ProductDetails';
import PdpNoProductData from '@/components/containers/pdp/PdpNoProductData';
import {PdlLoading} from '@pedal/pdl-loading';
import {mapActions, mapState, mapMutations, mapGetters} from 'vuex';
import UserOccApi from '@/api/user-occ.js';
import {imageSortByWeight} from '@/utils/product/product-helpers.js';
import {Cloudinary} from '@/utils/image/cloudinary-helpers';

export default {
  name: 'BuyZoneContainer',

  components: {PdpNoProductData, PdlLoading},

  props: {
    copyPositioningStatement: {
      type: String,
      default: '',
    },
    productId: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapState('backend', [
      'powerReviewsLocale',
      'powerReviewsMerchantGroupId',
      'powerReviewsMerchantId',
      'powerReviewsApiKey',
      'b2b',
      'encodedContextPath',
      'baseUrl',
      'companyName',
      'companyId',
      'appleTableHeaders',
      'locale',
      'hidePrices',
      'eCommAvailable',
      'isLocallyActive',
      'isProductCompareEnabledOnSite',
      'isProjectOneConfiguratorActive',
      'kioskModeEnabledOnSite',
      'CSRFToken',
      'maxPdpThumbnails',
      'rpaFunctionalityActive',
      'trekBrandNavURL',
      'hasUserAccessToBuyingZone',
    ]),
    ...mapState('user', ['userEmail', 'userCustomerId']),
    ...mapState('productStockData', ['stockMessage']),
    ...mapState('retailerSearch', ['isLocationPostalCodeCorrect', 'retailerData', 'isLoadingRetailerData']),
    ...mapState('productDetails', ['category']),
    ...mapState('familyDetails', ['familyDetailsData', 'selectedModelId', 'models']),
    ...mapGetters('familyDetails', ['selectedModel']),
    ...mapGetters('productDetails', ['locallyVariantProduct']),
    ...mapGetters('backend', ['occUrlParams']),
    ...mapGetters('checkout', ['isRestrictedToRetailer']),

    productDetailsComponent() {
      return this.b2b ? B2bProductDetails : B2cProductDetails;
    },
    isPdp() {
      return !this.models.length;
    },
    options() {
      return {
        hidePrices: this.hidePrices,
        eCommAvailable: this.eCommAvailable,
        appleTableHeaders: this.appleTableHeaders,
        kioskModeEnabledOnSite: this.kioskModeEnabledOnSite,
        token: this.CSRFToken,
        productCompareEnabledOnSite: this.isProductCompareEnabledOnSite,
        maxPdpThumbnails: this.maxPdpThumbnails,
        rpaFunctionalityActive: this.rpaFunctionalityActive,
        brandCartUrl: `${this.trekBrandNavURL}/cart`,
        isLocallyActive: this.isLocallyActive,
        isProjectOneConfiguratorActive: this.isProjectOneConfiguratorActive,
        siteLocale: this.locale,
        encodedContextPath: this.encodedContextPath,
        companyName: this.companyName,
        companyId: this.companyId,
        baseUrl: this.baseUrl,
        hasUserAccessToBuyingZone: this.hasUserAccessToBuyingZone,
      };
    },
  },
  watch: {
    async selectedModel() {
      if (!this.selectedModel || this.b2b) return;
      BreadCrumbMarkup.watch(this.selectedModel, this.category);
      if (!this.selectedModel.powerReviewsID) return;
      this.setPowerReviewsProductData(this.selectedModel);
      this.fetchProductReviews(this.selectedModel.powerReviewsID).then((reviewsData) => {
        SchemaMarkup.init().addProductDetails(this.selectedModel).addPowerReviews(reviewsData).injectMarkup();
      });
    },
    selectedModelId() {
      this.setProduct(this.selectedModel);
      this.setIsBikeCustomizable(!!this.selectedModel?.customProductStartingPoint);
    },
  },
  mounted() {
    this.loadProductData();
    if (this.isRestrictedToRetailer) {
      this.fetchUserCompetency();
    }
  },

  methods: {
    ...mapMutations('productDetails', ['setProduct', 'setIsBikeCustomizable']),
    ...mapMutations('powerReviews', ['setProductDetails', 'setIsWindowPwrLoaded', 'setIsLoadingScript']),
    ...mapActions('powerReviews', ['fetchProductReviews']),
    ...mapActions('productBundle', ['addBundleTemplates']),

    initializeLegacyPdpHandlers() {
      // This method must be refactored.
      const event = new Event('init-pdp');
      window.dispatchEvent(event);
    },
    loadProductData() {
      this.loading = true;

      if (this.isPdp) {
        ProductApi.loadProduct(this.productId, this.b2b)
          .then((data) => {
            let productData = data;
            if (productData?.images) {
              Object.entries(productData?.images).forEach((entry) => {
                return imageSortByWeight(productData?.images[entry[0]]);
              });
            }
            const variantsWithBundle = productData.variants.filter(
              (variant) => variant.bundleTemplateIdList?.length > 0
            );

            if (variantsWithBundle.length > 0) {
              const bundleTemplateIdList = [];
              const skusWithBundle = [];
              variantsWithBundle.forEach((variant) => {
                skusWithBundle.push(variant.code);
                bundleTemplateIdList.push(variant.bundleTemplateIdList[0]);
              });

              this.addBundleTemplates({id: bundleTemplateIdList, sku: skusWithBundle});
            }

            this.$nextTick(() => {
              this.initializeLegacyPdpHandlers();
              this.loading = false;
            });

            this.setPowerReviewsProductData(productData);
            this.$emit('is-archived', productData.isArchived);
            this.setProduct(productData);
          })
          .catch((reason) => {
            console.error(reason.message);
            this.loading = false;
          });
      } else {
        if (this.selectedModel?.powerReviewsID) this.setPowerReviewsProductData(this.selectedModel);
        this.setProduct(this.selectedModel);
        this.setIsBikeCustomizable(!!this.selectedModel?.customProductStartingPoint);
        this.loading = false;
      }
    },
    async fetchUserCompetency() {
      try {
        const competency = await UserOccApi.getUserCompetency(this.occUrlParams.userId);
        this.updateTrekUCompetency(competency);
      } catch (err) {
        console.error(err);
      }
    },
    setPowerReviewsProductData(product) {
      let imageKey;
      if (product?.images) {
        imageKey = Object.keys(product?.images).length > 0 ? Object.keys(product.images)[0] : undefined;
      }
      const pRImageUrl =
        imageKey && product?.images[imageKey]?.length > 0
          ? Cloudinary.createAssetUrl(product.images[imageKey][0]?.assetId)
          : undefined;

      let pageId = product.powerReviewsID ? product.powerReviewsID : product.code;
      let powerReviewsProductDetails = {
        pr_product_image: pRImageUrl,
        pr_page_id: pageId,
        pr_product_url: product.powerReviewsProductUrl,
        pr_product_name: product.name,
        pr_product_description: product.description,
        pr_product_category_name: product.defaultCategory,
        pr_merchant_user_id: this.userCustomerId,
        pr_merchant_user_email: this.userEmail,
        pr_api_key: this.powerReviewsApiKey,
        pr_locale: this.powerReviewsLocale,
        pr_merchant_group_id: this.powerReviewsMerchantGroupId,
        pr_merchant_id: this.powerReviewsMerchantId,
        pr_write_review: `${product.powerReviewsProductUrl}writeReview?`,
        pr_product_manufacturer_id: product.code,
        pr_product_upc: product.ean,
        pr_product_brand_name: product.variants[0]?.brandNameFull,
        pr_product_price: product.prices.consumerPrice?.low?.value,
        pr_product_in_stock: product.isInStockOnline,
      };
      this.setProductDetails(powerReviewsProductDetails);
    },
  },
};
</script>
